export type SpeakerMap = Record<
  string,
  { entity_type: string; entity_id: string }
>;

/**
 * Parse and validate a speaker map JSON string.
 * Returns a null-prototype object with validated entries only.
 * Entries missing entity_type or entity_id fields are silently discarded.
 * Intentional tightening: previously MeetingNotesTab accepted any JSON object
 * without per-field validation.
 */
export function parseSpeakerMap(raw: string | undefined): SpeakerMap {
  if (!raw) {
    return Object.create(null) as SpeakerMap;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return Object.create(null) as SpeakerMap;
    }
    const validated: SpeakerMap = Object.create(null) as SpeakerMap;
    for (const [key, value] of Object.entries(
      parsed as Record<string, unknown>
    )) {
      if (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as Record<string, unknown>).entity_type === 'string' &&
        typeof (value as Record<string, unknown>).entity_id === 'string'
      ) {
        validated[key] = value as { entity_type: string; entity_id: string };
      }
    }
    return validated;
  } catch (e) {
    console.warn('Failed to parse ahoy_speaker_map JSON', e);
    return Object.create(null) as SpeakerMap;
  }
}

/**
 * Enriches text by replacing speaker display names with entity tags using
 * the stored speaker map. Sorts names longest-first to avoid partial replacements.
 */
export function enrichWithSpeakerMap(
  text: string,
  speakerMap: Record<string, { entity_type: string; entity_id: string }>
): string {
  const entries = Object.entries(speakerMap).sort(
    ([a], [b]) => b.length - a.length
  );

  let result = text;

  for (const [name, value] of entries) {
    if (
      !value ||
      typeof value !== 'object' ||
      !value.entity_type ||
      !value.entity_id
    ) {
      continue;
    }
    const { entity_type, entity_id } = value;
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const entityTag = `<entity type="${entity_type}" id="${entity_id}">${name}</entity>`;

    // Replace bold format: **Name**
    result = result.replace(
      new RegExp(`\\*\\*${escapedName}\\*\\*`, 'gi'),
      () => `**${entityTag}**`
    );

    // Replace em dash format: — Name
    result = result.replace(
      new RegExp(`(— )${escapedName}(?=[\\s,;:.!?)\\]\\n]|$)`, 'gi'),
      (match, prefix: string) => `${prefix}${entityTag}`
    );

    // Replace plain occurrences not inside code blocks
    // Split on backtick-wrapped content to skip code spans
    const parts = result.split(/(`.+?`)/);
    result = parts
      .map((part, i) => {
        // Odd indices are code spans — leave them alone
        if (i % 2 !== 0) {
          return part;
        }
        return part.replace(
          new RegExp(
            `(?<!<entity[^>]{0,200})(?<![\\w])${escapedName}(?![\\w])(?![^<]{0,200}<\\/entity>)`,
            'gi'
          ),
          () => entityTag
        );
      })
      .join('');
  }

  return result;
}
